/**
 * Markdown field with a formatted (WYSIWYG) and a plain Markdown view.
 *
 * Drop-in for a `<textarea>` holding Markdown: the parent keeps the text and
 * stores Markdown as before. The formatted view is the default; the choice is
 * remembered per browser. Guidance comments (`<!-- … -->`) and placeholders
 * (`{{name}}`) are shown specially and survive both views unchanged.
 *
 * Usage:
 *   <label id="x-label" htmlFor="x">Text</label>
 *   <MarkdownEditor id="x" labelledBy="x-label" value={text} onChange={setText} />
 */
import { lazy, Suspense, useState, type ReactNode } from 'react';

import Spinner from '../Spinner';
import './markdown-editor.css';

const WysiwygEditor = lazy(() => import('./WysiwygEditor'));

export type MarkdownEditorMode = 'formatted' | 'markdown';

const MODE_STORAGE_KEY = 'assetradar.markdownEditor.mode';

function storedMode(): MarkdownEditorMode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === 'markdown' ? 'markdown' : 'formatted';
  } catch {
    return 'formatted';
  }
}

function rememberMode(mode: MarkdownEditorMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Storage unavailable (private mode): the choice simply is not remembered.
  }
}

export interface MarkdownEditorProps {
  /** Id of the Markdown textarea; the formatted view is labelled via `labelledBy`. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Id of the visible label element (needed for the formatted view). */
  labelledBy?: string;
  describedBy?: string;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Enforced by the Markdown view; the parent shows the count and blocks saving. */
  maxLength?: number;
  /** Visible rows of the Markdown view. */
  rows?: number;
}

export function MarkdownEditor({
  id,
  value,
  onChange,
  labelledBy,
  describedBy,
  readOnly = false,
  disabled = false,
  placeholder,
  maxLength,
  rows = 17,
}: MarkdownEditorProps): ReactNode {
  const [mode, setMode] = useState<MarkdownEditorMode>(storedMode);
  const [showGuidance, setShowGuidance] = useState(true);
  const hasGuidance = value.includes('<!--');

  const choose = (next: MarkdownEditorMode): void => {
    setMode(next);
    rememberMode(next);
  };

  return (
    <div className={`md-editor${showGuidance ? '' : ' md-editor--hide-guidance'}`}>
      <div className="md-editor-head">
        <div className="seg" role="group" aria-label="Editoransicht">
          <button type="button" className={mode === 'formatted' ? 'on' : undefined} aria-pressed={mode === 'formatted'} onClick={() => choose('formatted')}>
            Formatiert
          </button>
          <button type="button" className={mode === 'markdown' ? 'on' : undefined} aria-pressed={mode === 'markdown'} onClick={() => choose('markdown')}>
            Markdown
          </button>
        </div>
        {mode === 'formatted' && hasGuidance ? (
          <label className="check md-editor-guidance-toggle">
            <input type="checkbox" checked={showGuidance} onChange={(event) => setShowGuidance(event.target.checked)} />
            Anleitungen anzeigen
          </label>
        ) : null}
      </div>
      {mode === 'markdown' ? (
        <textarea
          id={id}
          className="md-editor-source"
          rows={rows}
          spellCheck={false}
          value={value}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Suspense fallback={<div className="md-editor-loading"><Spinner label="Editor wird geladen…" /></div>}>
          <WysiwygEditor
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            disabled={disabled}
            placeholder={placeholder}
            labelledBy={labelledBy}
            describedBy={describedBy}
          />
        </Suspense>
      )}
    </div>
  );
}

export default MarkdownEditor;
